import {
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { LitigationInformationService } from "./litigation-information.service";
import { environment } from "src/environments/environment.prod";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";

interface LitigationRow {
  caseNumber: string;
  ndaBrand: string;
  plaintiff: string;
  defendant: string;
  andaNumber: string;
  filedAnda: string;
  courtName: string;
  judgeName: string;
  patentsAtIssue: string;
  pdfFile: string;
  caseStatus: string;
  terminationDate: string;
  filedDate: string;
  litigationCategory?: string;
  appealCases?: any[];
  expanded?: boolean;
}

@Component({
  selector: 'app-litigation-information',
  templateUrl: './litigation-information.component.html',
  styleUrl: './litigation-information.component.scss'
})
export class LitigationInformationComponent implements OnInit {
  @Input() BrandName: string;
  @Input() NDAnumber: string | number;
  @Input() saveOldData: any;

  @Output() litigationHeaderUpdated = new EventEmitter<string>();

  loading = false;
  errorMessage = "";
  litigationData: LitigationRow[] = [];
  otherLitigationData: LitigationRow[] = [];

  constructor(
    private litigationService: LitigationInformationService,
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.loadLitigationData();
  }

  private buildPayload(): { "NDA Number": string; "Brand Name": string } {
    let nda = this.NDAnumber;
    let brand = this.BrandName;

    if (!nda && this.saveOldData) {
      nda = this.saveOldData["NDA number"];
    }
    if (!brand && this.saveOldData) {
      brand = this.saveOldData["Brand Name/Proprietary Name"];
    }

    return {
      "NDA Number": nda ? nda.toString() : "",
      "Brand Name": brand || "",
    };
  }

  private mapLitigationRow(row: any): LitigationRow {
    const judgeRaw = row["Judge"] || row["Judge Name"];
    const patentsRaw = row["Patents at issue"] || row["Patents in Case"];
    const filedAndaRaw = row["Filed ANDA"];
    const categoryRaw = row["Litigation Category"] || row["Litigation_Category"] || row["litigation_category"];

    return {
      caseNumber: row["Case Number"] || "N/A",
      ndaBrand: `${row["NDA Number"] || ""} - ${row["Brand Name"] || ""}`.replace(/^ - /, "").trim(),
      plaintiff: row["Plaintiff"] || row["Plaintiffs"] || "N/A",
      defendant: row["Defendant"] || row["Defendants"] || "N/A",
      andaNumber: row["ANDA Number"] || "N/A",
      filedAnda: Array.isArray(filedAndaRaw)
        ? filedAndaRaw.join(", ")
        : filedAndaRaw || "N/A",
      courtName: row["Court Name"] || "N/A",
      judgeName: Array.isArray(judgeRaw)
        ? judgeRaw.join(", ")
        : judgeRaw || "N/A",
      patentsAtIssue: Array.isArray(patentsRaw)
        ? patentsRaw.join(", ")
        : patentsRaw || "N/A",
      pdfFile: row["PDF File"] || row["PDF"] || "",
      caseStatus: row["Case Status"] || "N/A",
      terminationDate: row["Termination Date"] || "N/A",
      filedDate: row["Field ID"] || row["Filed Date"] || "N/A",
      litigationCategory: categoryRaw || "N/A",
      appealCases: row["AppealCases"] || row["appeal_cases"] || [],
      expanded: false,
    };
  }

  loadLitigationData(): void {
    const payload = this.buildPayload();

    if (!payload["NDA Number"] || !payload["Brand Name"]) {
      this.errorMessage = "NDA Number or Brand Name is missing.";
      return;
    }

    this.loading = true;
    this.errorMessage = "";

    forkJoin({
      litigation: this.litigationService.getLitigationData(payload).pipe(
        catchError((err) => {
          console.error("Error fetching litigation data:", err);
          return of({ data: [] });
        })
      ),
      otherLitigation: this.litigationService.getOtherLitigationData(payload).pipe(
        catchError((err) => {
          console.error("Error fetching other litigation data:", err);
          return of({ data: [] });
        })
      ),
    }).subscribe(
      (results: { litigation: any; otherLitigation: any }) => {
        this.loading = false;

        if (results.litigation && Array.isArray(results.litigation.data)) {
          this.litigationData = results.litigation.data.map((row: any) => this.mapLitigationRow(row));
        } else {
          this.litigationData = [];
        }

        if (results.otherLitigation && Array.isArray(results.otherLitigation.data)) {
          this.otherLitigationData = results.otherLitigation.data.map((row: any) => this.mapLitigationRow(row));
        } else {
          this.otherLitigationData = [];
        }

        const totalCount = this.litigationData.length + this.otherLitigationData.length;
        const headerText =
          totalCount > 0
            ? `LITIGATION INFORMATION - ${totalCount} case(s) found`
            : "LITIGATION INFORMATION - No cases found";
        this.litigationHeaderUpdated.emit(headerText);

        if (this.litigationData.length === 0 && this.otherLitigationData.length === 0) {
          this.errorMessage = "No litigation data available.";
        }

        this.ref.detectChanges();
      },
      (err) => {
        this.loading = false;
        console.error("Error fetching litigation data:", err);
        this.errorMessage = "Failed to fetch litigation data.";
        this.toastr.error(this.errorMessage, "Error");
        this.ref.detectChanges();
      }
    );
  }

  openPdf(row: LitigationRow): void {
    if (!row || !row.pdfFile) {
      this.toastr.error("PDF file path not available.", "Error");
      return;
    }

    const url = `${environment.ip}pm_backend/${row.pdfFile}`;
    window.open(url, "_blank");
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  hasAppealCases(appeals: any[] | undefined): boolean {
    if (!appeals || !Array.isArray(appeals)) return false;
    return appeals.some((appeal: any) => !appeal['is_deleted']);
  }

  getFilteredAppeals(appeals: any[] | undefined): any[] {
    if (!appeals || !Array.isArray(appeals)) return [];
    return appeals.filter((appeal: any) => !appeal['is_deleted']);
  }

  toggleExpand(row: LitigationRow): void {
    row.expanded = !row.expanded;
  }

  openAppealPdf(pdfPath: string): void {
    if (!pdfPath || pdfPath === 'N/A') {
      this.toastr.error("PDF file path not available.", "Error");
      return;
    }
    const url = `${environment.ip}pm_backend/${pdfPath}`;
    window.open(url, "_blank");
  }
}